// src/components/import/ImportHistory.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { FaTrash } from "react-icons/fa";

import Sidebar from "../Sidebar";
import TopBar from "../TopBar";

import {
  ImportWrapper,
  Content,
  PageBody,
  Card,
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "./ImportComponentStyles";

/* ===== helpers ===== */

const formatDatePL = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("pl-PL");
  } catch {
    return "—";
  }
};

/* ===== styled ===== */

const CardNoClip = styled(Card)`
  overflow: visible;
`;

const SectionTitle = styled.h2`
  margin: 0 0 14px;
`;

const HistoryScroll = styled.div`
  max-height: 480px;
  overflow: auto;
  border-radius: 12px;
  border: 1px solid #e8eef7;
  background: #fff;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
  &::-webkit-scrollbar { height: 10px; width: 10px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; }
  &::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
`;

const HistoryTable = styled(Table)`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  thead ${TableRow} {
    background: linear-gradient(180deg, #f7f9fc 0%, #eef3fb 100%);
  }
  thead ${TableHeader} {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f7f9fc;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #334155;
    padding: 14px 16px;
    border-bottom: 2px solid #d8e3f6;
  }
  tbody ${TableRow}:nth-child(odd) { background: #fcfdff; }
  tbody ${TableRow}:hover { background: #f2f7ff; transition: background 0.15s; }
  tbody ${TableCell} { padding: 12px 16px; border-bottom: 1px solid #eef2f7; }

  @media (max-width: 640px) {
    thead { display: none; }
    tbody ${TableRow} {
      display: block;
      background: #fff;
      margin: 10px 0;
      border: 1px solid #eef2f7;
      border-radius: 10px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    tbody ${TableCell} {
      display: grid;
      grid-template-columns: 140px 1fr;
      gap: 8px;
      padding: 10px 12px;
      border: none;
      border-bottom: 1px solid #f1f4f8;
    }
    tbody ${TableCell}:last-child { border-bottom: none; }
    tbody ${TableCell}::before {
      content: attr(data-label);
      font-weight: 700;
      color: #4b5563;
    }
  }
`;

const UserChip = styled.span`
  display: inline-block;
  background: #eef4ff;
  color: #274690;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
`;

const EmptyState = styled.div`
  padding: 18px;
  text-align: center;
  color: #6b7280;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const IconButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  color: #b91c1c;
  padding: 6px;
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    background: #fee2e2;
    color: #7f1d1d;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  svg { width: 16px; height: 16px; }
`;

/* Modal potwierdzenia */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(9, 12, 20, 0.55);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 18px 20px;
  background: linear-gradient(90deg, #fef2f2, #fff);
  border-bottom: 1px solid #fde2e2;
  h3 {
    margin: 0;
    font-size: 18px;
    color: #991b1b;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ModalBody = styled.div`
  padding: 16px 20px;
  color: #374151;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 14px 20px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #eef2f7;
`;

const DangerBtn = styled.button`
  appearance: none;
  border: 1px solid #fca5a5;
  background: #fee2e2;
  color: #b91c1c;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #fecaca; border-color: #f87171; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const GhostBtn = styled.button`
  appearance: none;
  border: 1px solid #dbe3ef;
  background: #fff;
  color: #334155;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f8fafc; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* ===== component ===== */

export default function ImportHistory() {
  const [importHistory, setImportHistory] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { _id, fileName }
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem("token");
  const axiosCfg = { headers: { Authorization: token } };

  const fetchHistory = async () => {
    const res = await axios.get(
      "https://server229256.nazwa.pl/api/import-history",
      axiosCfg
    );
    setImportHistory(res.data || []);
  };

  useEffect(() => {
    fetchHistory().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askDelete = (row) => {
    setPendingDelete({ _id: row._id, fileName: row.fileName || "bez nazwy" });
    setConfirmOpen(true);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setPendingDelete(null);
    setDeleting(false);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleting(true);
    try {
      await axios.delete(
        `https://server229256.nazwa.pl/api/import-history/${pendingDelete._id}`,
        axiosCfg
      );
      setImportHistory((prev) => prev.filter((r) => r._id !== pendingDelete._id));
      cancelDelete();
    } catch (err) {
      console.error("Błąd kasowania historii:", err?.response?.data || err.message);
      setDeleting(false);
      alert("Nie udało się usunąć rekordu historii. Spróbuj ponownie.");
    }
  };

  return (
    <ImportWrapper>
      <Sidebar />

      <Content>
        <TopBar />

        <PageBody>
          <CardNoClip>
            <SectionTitle>Historia importów</SectionTitle>

            <HistoryScroll>
              <HistoryTable>
                <thead>
                  <TableRow>
                    <TableHeader>Nazwa pliku</TableHeader>
                    <TableHeader>Data importu</TableHeader>
                    <TableHeader>Użytkownik</TableHeader>
                    <TableHeader>Akcje</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {importHistory.length > 0 ? (
                    importHistory.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell data-label="Nazwa pliku">
                          {row.fileName || "—"}
                        </TableCell>
                        <TableCell data-label="Data importu">
                          {formatDatePL(row.importDate)}
                        </TableCell>
                        <TableCell data-label="Użytkownik">
                          {row.user ? <UserChip>{row.user}</UserChip> : "—"}
                        </TableCell>
                        <TableCell data-label="Akcje">
                          <ActionsCell>
                            <IconButton
                              onClick={() => askDelete(row)}
                              title="Usuń rekord"
                            >
                              <FaTrash />
                            </IconButton>
                          </ActionsCell>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan="4">
                        <EmptyState>Brak historii importów.</EmptyState>
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </HistoryTable>
            </HistoryScroll>
          </CardNoClip>
        </PageBody>
      </Content>

      {/* Modal potwierdzenia — usuwanie rekordu historii */}
      {confirmOpen && (
        <ModalOverlay onClick={cancelDelete}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Potwierdzenie usunięcia</h3>
            </ModalHeader>
            <ModalBody>
              <p style={{ marginTop: 0 }}>
                Czy na pewno chcesz usunąć <strong>rekord historii importu</strong>
                {pendingDelete?.fileName ? (
                  <> dla pliku <strong>„{pendingDelete.fileName}”</strong></>
                ) : null}
                ?
              </p>
              <p
                style={{
                  margin: 0,
                  color: "#9a3412",
                  background: "#fff7ed",
                  border: "1px solid #ffedd5",
                  padding: "10px 12px",
                  borderRadius: 8,
                }}
              >
                <strong>Uwaga:</strong> usuwasz jedynie wpis w historii.
                Powiązane dane (np. sprzedaż/zakupy) <strong>nie będą usunięte</strong>
                i nadal będą widoczne w raportach.
              </p>
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={cancelDelete} disabled={deleting}>
                Anuluj
              </GhostBtn>
              <DangerBtn onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Usuwanie…" : "Usuń rekord"}
              </DangerBtn>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </ImportWrapper>
  );
}
